import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import './AccountSettings.css';

function ChangeEmailForm({ onClose, currentEmail }) {
  const { t } = useTranslation(['profile', 'shared']);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const trimmedEmail = newEmail.trim();

    // Walidacja
    if (!trimmedEmail) {
      setError(t('profile.settings.pages.account.changeEmailForm.errorEmpty'));
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError(t('profile.settings.pages.account.changeEmailForm.errorInvalid'));
      return;
    }

    if (trimmedEmail.toLowerCase() === (currentEmail || '').toLowerCase()) {
      setError(t('profile.settings.pages.account.changeEmailForm.errorSame'));
      return;
    }

    setLoading(true);

    try {
      // Aktualizacja email w Supabase Auth
      // Supabase automatycznie wyśle email weryfikacyjny
      const { error: updateError } = await supabase.auth.updateUser({
        email: trimmedEmail
      });

      if (updateError) {
        // Lepsza obsługa błędów Supabase
        let errorMessage = updateError.message;
        
        // Mapowanie typowych błędów Supabase na czytelne komunikaty
        if (updateError.message?.includes('Invalid email') || 
            updateError.message?.includes('email address is invalid')) {
          errorMessage = t('profile.settings.pages.account.changeEmailForm.errorInvalidEmail');
        } else if (updateError.message?.includes('already registered') ||
                   updateError.message?.includes('already exists')) {
          errorMessage = t('profile.settings.pages.account.changeEmailForm.errorEmailExists');
        } else if (updateError.message?.includes('rate limit') ||
                   updateError.message?.includes('too many requests')) {
          errorMessage = t('profile.settings.pages.account.changeEmailForm.errorRateLimit');
        }
        
        throw new Error(errorMessage);
      }

      setSuccess(true);
      
      // Odśwież dane użytkownika w tle (bez przeładowania strony)
      // Email nie zmieni się od razu - dopiero po weryfikacji linku w emailu
      // Więc nie ma potrzeby odświeżać danych teraz
    } catch (err) {
      console.error('Error updating email:', err);
      setError(err.message || t('profile.settings.pages.account.changeEmailForm.errorUpdate'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="account-form-container">
      <h3 className="account-form-title">
        {t('profile.settings.pages.account.changeEmailForm.title')}
      </h3>

      <form onSubmit={handleSubmit} className="account-form">
        <div className="account-form-field">
          <label className="account-form-label">
            {t('profile.settings.pages.account.changeEmailForm.currentEmail')}
          </label>
          <div className="account-form-value">
            {currentEmail || t('profile.settings.pages.account.notSet')}
          </div>
        </div>

        <div className="account-form-field">
          <label htmlFor="newEmail" className="account-form-label">
            {t('profile.settings.pages.account.changeEmailForm.newEmail')}
          </label>
          <input
            id="newEmail"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder={t('profile.settings.pages.account.changeEmailForm.newEmailPlaceholder')}
            className="account-form-input"
            disabled={loading || success}
            required
          />
        </div>

        {error && (
          <div className="account-form-error">
            {error}
          </div>
        )}

        {success && (
          <div className="account-form-success">
            {t('profile.settings.pages.account.changeEmailForm.success')}
          </div>
        )}

        <div className="account-form-info">
          {t('profile.settings.pages.account.changeEmailForm.info')}
        </div>

        <div className="account-form-actions">
          <button
            type="button"
            onClick={onClose}
            className="account-form-button account-form-button-secondary"
            disabled={loading}
          >
            {success ? t('common.close') : t('common.cancel')}
          </button>
          {!success && (
            <button
              type="submit"
              className="account-form-button account-form-button-primary"
              disabled={loading || !newEmail.trim()}
            >
              {loading
                ? t('profile.settings.pages.account.changeEmailForm.sending')
                : t('profile.settings.pages.account.changeEmailForm.sendButton')
              }
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default ChangeEmailForm;

